import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import {
  Button,
  Icon,
  OwlMark,
  Pill,
  Wordmark,
  cn,
  type IconName,
} from "@wiseowl/design-system";
import { useAuth } from "../lib/auth-context.js";
import { api } from "../lib/api.js";
import { isApiError } from "@wiseowl/shared";
import { ReflectFlow } from "../components/reflect-flow.js";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Show a small mustard dot to the right (Companion only). */
  hasOwl?: boolean;
}

const NAV: NavItem[] = [
  { to: "/home",      label: "Home",      icon: "home" },
  { to: "/reflect",   label: "Reflect",   icon: "book" },
  { to: "/companion", label: "Companion", icon: "chat", hasOwl: true },
  { to: "/library",   label: "Library",   icon: "chart" },
];

/** Context handed to routed children so any view can open the reflection. */
export interface AppShellContext {
  openReflect: () => void;
}

/**
 * Authed app shell.
 *
 * Desktop (≥900px):
 *   - 268px sticky sidebar on the left (OwlMark + Wordmark, nav rows,
 *     "Today's reset" CTA, streak card, profile row).
 *   - Sticky topbar with search + bell + "Talk to Wise Owl" + mustard
 *     "Begin today's reset" CTA.
 *
 * Mobile (<900px):
 *   - Sticky topbar with Wordmark + search/bell icons + avatar.
 *   - Bottom-nav (Home / Reflect / [center mustard CTA] / Companion /
 *     Library), safe-area aware. The center button opens ReflectFlow.
 *
 * Reflect and Companion are full-screen overlays, not routes — their
 * state lives here so any nav item can open them.
 */
export const AppLayout = () => {
  const { user, signOut, isDemo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reflecting, setReflecting] = useState(false);
  const [streakDays, setStreakDays] = useState(0);

  // Honest streak for the sidebar card — no fake data, no first-run shame.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const s = await api<{ streak: { currentDays: number } }>("/streak?practice=daily-reset");
      if (!cancelled && !isApiError(s)) setStreakDays(s.data.streak.currentDays);
    })();
    return () => { cancelled = true; };
  }, []);

  const onNav = (item: NavItem) => navigate(item.to);

  const initial = user?.name?.[0]?.toUpperCase() ?? "?";

  return (
    <div className="flex min-h-screen bg-cream text-ink">
      {/* ───── Desktop sidebar (hidden <900px) ───── */}
      <aside className="sticky top-0 hidden h-screen w-[268px] shrink-0 flex-col border-r border-line bg-paper md:flex">
        <div className="flex items-center gap-2.5 px-[22px] pb-2 pt-6">
          <OwlMark size={32} />
          <Wordmark size={20} />
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 p-3">
          {NAV.map((item) => (
            <SidebarRow
              key={item.to}
              item={item}
              active={location.pathname.startsWith(item.to)}
              onClick={() => onNav(item)}
            />
          ))}

          <div className="h-3.5" />

          <button
            type="button"
            onClick={() => setReflecting(true)}
            className="relative mt-1.5 flex items-center gap-2.5 overflow-hidden rounded-md bg-ink px-3.5 py-3 text-left font-sans text-small font-semibold text-cream"
          >
            <Icon name="spark" size={18} className="text-mustard" />
            <span>Today&apos;s reset</span>
            <span className="relative ml-auto">
              <Icon name="arrow" size={16} className="text-cream" />
            </span>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-7 -top-7 h-24 w-24 rounded-pill opacity-35"
              style={{ background: "radial-gradient(circle, rgb(var(--wo-mustard)) 0%, transparent 70%)" }}
            />
          </button>
        </nav>

        <div className="flex flex-col gap-2.5 border-t border-line p-3">
          <div className="rounded-md bg-cream-2 p-3.5">
            <div className="flex items-center gap-2">
              <Icon name="flame" size={16} className={streakDays > 0 ? "text-mustard" : "text-muted-2"} />
              <div className="text-small font-bold text-ink">
                {streakDays > 0 ? `${streakDays} day streak` : "Your first reset is waiting"}
              </div>
            </div>
            <div className="mt-2 flex gap-[3px]">
              {Array.from({ length: 21 }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "h-1.5 flex-1 rounded-sm",
                    i < Math.min(streakDays, 21) ? "bg-mustard" : "bg-line-strong",
                  )}
                />
              ))}
            </div>
            <div className="mt-2 text-eyebrow text-muted">
              {streakDays >= 21
                ? "21-Day reset complete"
                : streakDays > 0
                  ? `${21 - streakDays} days to 21-Day reset`
                  : "No streaks to defend — just begin"}
            </div>
          </div>

          <div className="flex items-center gap-2.5 px-1 py-2">
            <div className="grid h-9 w-9 place-items-center rounded-pill bg-teal text-small font-bold text-cream">
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-small font-semibold text-ink">{user?.name}</div>
              <div className="truncate text-eyebrow text-muted">{user?.email}</div>
            </div>
            <button
              type="button"
              onClick={signOut}
              aria-label="Sign out"
              className="text-muted hover:text-ink"
            >
              <Icon name="settings" size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* ───── Main column ───── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          name={user?.name}
          initial={initial}
          isDemo={isDemo}
          onBeginReset={() => setReflecting(true)}
          onCompanion={() => navigate("/companion")}
        />

        <main className="mx-auto w-full max-w-6xl flex-1 px-5 pb-24 pt-6 md:px-10 md:pt-8">
          <Outlet context={{ openReflect: () => setReflecting(true) } satisfies AppShellContext} />
        </main>

        <BottomNav
          activePath={location.pathname}
          onNavigate={onNav}
          onBeginReset={() => setReflecting(true)}
        />
      </div>

      {/* ───── Overlays ───── */}
      {reflecting && (
        <ReflectFlow
          onClose={() => setReflecting(false)}
          onComplete={() => {
            setReflecting(false);
            navigate("/library");
          }}
        />
      )}
    </div>
  );
};

/* ───── Sub-components ───── */

const SidebarRow = ({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "relative flex items-center gap-3 rounded-[12px] px-3.5 py-2.5 text-left text-small font-semibold transition-colors duration-fast ease-soft",
      active
        ? "bg-teal text-cream hover:bg-teal-2"
        : "text-ink-soft hover:bg-ink/[0.04] hover:text-ink",
    )}
  >
    <Icon name={item.icon} size={20} className={active ? "text-cream" : "text-ink-soft"} />
    <span>{item.label}</span>
    {item.hasOwl && (
      <span
        aria-hidden="true"
        className="ml-auto h-[7px] w-[7px] rounded-pill bg-mustard ring-[3px] ring-mustard-soft"
      />
    )}
  </button>
);

const TopBar = ({
  name,
  initial,
  isDemo,
  onBeginReset,
  onCompanion,
}: {
  name?: string | null;
  initial: string;
  isDemo: boolean;
  onBeginReset: () => void;
  onCompanion: () => void;
}) => (
  <>
    {/* Desktop top bar */}
    <header className="sticky top-0 z-10 hidden h-16 items-center justify-between gap-3 border-b border-line bg-cream/85 px-8 backdrop-blur md:flex">
      <div className="flex max-w-md flex-1 items-center gap-2.5 rounded-pill border border-line bg-paper px-3.5 py-2.5 text-small">
        <Icon name="search" size={16} className="text-muted" />
        <span className="text-muted">Search reflections, prompts, themes…</span>
        <span className="ml-auto rounded-sm bg-cream-2 px-1.5 py-0.5 font-mono text-eyebrow tracking-wider text-muted">
          ⌘K
        </span>
      </div>
      <div className="flex items-center gap-3">
        {isDemo && (
          <Pill tone="mustard" title="No backend connected — auth and data are local-only">
            Demo
          </Pill>
        )}
        <button type="button" aria-label="Notifications" className="grid h-9 w-9 place-items-center rounded-pill hover:bg-ink/[0.06]">
          <Icon name="bell" size={18} className="text-ink-soft" />
        </button>
        <Button variant="ghost" size="sm" leadingIcon={<Icon name="chat" size={16} />} onClick={onCompanion}>
          Talk it through
        </Button>
        <Button variant="mustard" size="sm" leadingIcon={<Icon name="spark" size={16} />} onClick={onBeginReset}>
          Begin today&apos;s reset
        </Button>
      </div>
    </header>

    {/* Mobile top bar */}
    <header className="sticky top-0 z-10 flex h-14 items-center justify-between gap-2 border-b border-line bg-cream/90 px-5 backdrop-blur md:hidden">
      <div className="flex items-center gap-2">
        <Wordmark size={18} />
        {isDemo && <Pill tone="mustard">Demo</Pill>}
      </div>
      <div className="flex items-center gap-1">
        <button type="button" aria-label="Notifications" className="grid h-9 w-9 place-items-center rounded-pill hover:bg-ink/[0.06]">
          <Icon name="bell" size={18} className="text-ink-soft" />
        </button>
        <Link
          to="/profile"
          aria-label={name ? `${name} — your account` : "Your account"}
          className="ml-1 grid h-8 w-8 place-items-center rounded-pill bg-teal text-meta font-bold text-cream"
        >
          {initial}
        </Link>
      </div>
    </header>
  </>
);

const BottomNav = ({
  activePath,
  onNavigate,
  onBeginReset,
}: {
  activePath: string;
  onNavigate: (item: NavItem) => void;
  onBeginReset: () => void;
}) => {
  const Item = ({ item, active }: { item: NavItem; active: boolean }) => (
    <button
      type="button"
      onClick={() => onNavigate(item)}
      className={cn(
        "relative flex flex-col items-center gap-1 rounded-sm px-3.5 py-2 transition-colors duration-fast",
        active ? "text-teal" : "text-ink-soft",
      )}
    >
      <Icon name={item.icon} size={22} />
      <span className="text-[11px] font-semibold">{item.label}</span>
      {item.hasOwl && (
        <span
          aria-hidden="true"
          className="absolute right-2.5 top-1.5 h-1.5 w-1.5 rounded-pill bg-mustard ring-2 ring-cream"
        />
      )}
    </button>
  );

  return (
    <nav
      className="sticky bottom-0 z-10 flex items-center justify-around border-t border-line bg-cream/[0.97] px-2 backdrop-blur-md md:hidden"
      style={{ paddingTop: 8, paddingBottom: "max(8px, env(safe-area-inset-bottom))" }}
    >
      <Item item={NAV[0]!} active={activePath.startsWith("/home")} />
      <Item item={NAV[1]!} active={activePath.startsWith("/reflect")} />
      <button
        type="button"
        onClick={onBeginReset}
        aria-label="Begin today's reset"
        className="-mt-5 grid h-14 w-14 place-items-center rounded-pill border-[3px] border-cream bg-mustard text-ink shadow-2 active:scale-[0.98]"
      >
        <Icon name="spark" size={22} />
      </button>
      <Item item={NAV[2]!} active={activePath.startsWith("/companion")} />
      <Item item={NAV[3]!} active={activePath.startsWith("/library")} />
    </nav>
  );
};
